import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  App as AntApp,
  Button,
  Divider,
  Empty,
  Flex,
  Input,
  Modal,
  Skeleton,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import type { CloudDeck } from '@lingoping/core';
import { DeckCard } from './DeckCard.js';
import { DeckPage } from './DeckPage.js';
import { DeckSession } from './DeckSession.js';
import { ImportPanel } from './ImportPanel.js';
import {
  CloudDeckWord,
  CloudDecksApi,
  DeckLists,
  getCloudDecksApi,
} from '../services/cloudDecks.js';
import { deckLearnQueue } from '../services/deckDecisions.js';
import {
  DeckProgress,
  EMPTY_LEARNER,
  LearnerState,
  classifyDeck,
  deckProgress,
  loadLearnerState,
} from '../services/deckStatus.js';
import { deckIdFromHash, hashForDeck, hashForTab } from '../options/tabs.js';
import { tourTarget } from '../options/tourTargets.js';

const { Text, Title } = Typography;

type View =
  | { kind: 'list' }
  | { kind: 'deck'; deckId: string }
  | { kind: 'session'; deckId: string; back: View }
  | { kind: 'import' };

interface Props {
  userId: string;
  /** False while another tab is showing. The swipe session then unmounts so its keyboard
   * shortcuts cannot fire while the user types elsewhere (choices are saved as they are made). */
  isActive?: boolean;
  onChanged: () => void;
}

const NO_GROUP = 'More decks';

/** Public decks under their group headings: groups in the order of their first deck. */
export function groupPublicDecks(decks: CloudDeck[]): { name: string; decks: CloudDeck[] }[] {
  const sorted = [...decks].sort(
    (a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title)
  );
  const groups = new Map<string, CloudDeck[]>();
  for (const deck of sorted) {
    const name = deck.group_name?.trim() || NO_GROUP;
    groups.set(name, [...(groups.get(name) ?? []), deck]);
  }
  return [...groups.entries()].map(([name, list]) => ({ name, decks: list }));
}

function setHash(hash: string): void {
  if (window.location.hash !== hash) history.replaceState(history.state, '', hash);
}

export const DecksTab: React.FC<Props> = ({ userId, isActive = true, onChanged }) => {
  const { message } = AntApp.useApp();
  const [api, setApi] = useState<CloudDecksApi | null>(null);
  const [lists, setLists] = useState<DeckLists | null>(null);
  const [wordsByDeck, setWordsByDeck] = useState<Record<string, CloudDeckWord[]>>({});
  const [learner, setLearner] = useState<LearnerState>(EMPTY_LEARNER);
  const [offline, setOffline] = useState(false);
  const [view, setView] = useState<View>(() => {
    const id = deckIdFromHash(window.location.hash);
    return id ? { kind: 'deck', deckId: id } : { kind: 'list' };
  });
  const [newDeckOpen, setNewDeckOpen] = useState(false);
  const [newDeckName, setNewDeckName] = useState('');
  const mounted = useRef(true);

  /** Tells the learner about changes the server refused (the limit of decks, for example). */
  const reportFailures = useCallback(
    async (service: CloudDecksApi) => {
      for (const text of await service.takeFailures()) message.warning(text);
    },
    [message]
  );

  const reloadLearner = useCallback(async () => setLearner(await loadLearnerState()), []);

  const loadWords = useCallback(async (service: CloudDecksApi, deckIds: string[]) => {
    const entries = await Promise.all(
      deckIds.map(async (id) => [id, (await service.readDeck(id))?.words ?? []] as const)
    );
    if (!mounted.current) return;
    setWordsByDeck((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
  }, []);

  // Saved copy first (instant, works offline), then the cloud in the background.
  useEffect(() => {
    mounted.current = true;
    void (async () => {
      const service = await getCloudDecksApi();
      if (!mounted.current) return;
      setApi(service);
      const saved = await service.readLists();
      setLists(saved);
      void reloadLearner();
      await loadWords(
        service,
        saved.decks.map((d) => d.id)
      );
      const { lists: fresh, online } = await service.refreshLists();
      if (!mounted.current) return;
      setLists(fresh);
      setOffline(!online);
      await reportFailures(service);
      const ids = fresh.decks.map((d) => d.id);
      await loadWords(service, ids);
      // Words of decks that changed (or were never loaded) come in one deck at a time.
      await service.syncWords(ids, (id) => void loadWords(service, [id]));
    })();
    return () => {
      mounted.current = false;
    };
  }, [loadWords, reloadLearner, reportFailures]);

  // Back on this tab: cards may have changed in the review window or another tab.
  useEffect(() => {
    if (!isActive) return;
    void reloadLearner();
    if (api) void api.readLists().then(setLists);
  }, [isActive, api, reloadLearner]);

  // Cards made in the background after "Learn it" change the progress.
  useEffect(() => deckLearnQueue.subscribe(() => void reloadLearner()), [reloadLearner]);

  // The open deck lives in the URL hash (#decks/<id>), so a refresh stays on it.
  const openView = useCallback((next: View) => {
    setView(next);
    if (next.kind === 'deck') setHash(hashForDeck(next.deckId));
    else if (next.kind === 'list' || next.kind === 'import') setHash(hashForTab('decks'));
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const id = deckIdFromHash(window.location.hash);
      if (id) setView({ kind: 'deck', deckId: id });
      else if (window.location.hash === hashForTab('decks')) setView({ kind: 'list' });
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Returning to this tab: the URL shows the deck that is open.
  useEffect(() => {
    if (isActive && view.kind === 'deck') setHash(hashForDeck(view.deckId));
  }, [isActive, view]);

  const refreshAfterChange = useCallback(async () => {
    if (!api) return;
    await reportFailures(api);
    setLists(await api.readLists());
    await reloadLearner();
    onChanged();
  }, [api, reloadLearner, onChanged, reportFailures]);

  const toggleFollow = async (deck: CloudDeck) => {
    if (!api) return;
    const following = lists?.followingId === deck.id;
    await api.follow(following ? null : deck.id);
    setLists(await api.readLists());
    message.success(
      following ? `Stopped following “${deck.title}”.` : `Following “${deck.title}”.`
    );
  };

  const createDeck = async () => {
    if (!api || !newDeckName.trim()) return;
    let deck: CloudDeck;
    try {
      deck = await api.createDeck(newDeckName);
    } catch (err) {
      message.error(err instanceof Error ? err.message : "The deck couldn't be created.");
      return;
    }
    setNewDeckOpen(false);
    setNewDeckName('');
    setLists(await api.readLists());
    await loadWords(api, [deck.id]);
    openView({ kind: 'deck', deckId: deck.id });
  };

  const progressOf = useMemo(() => {
    const result: Record<string, DeckProgress> = {};
    for (const deck of lists?.decks ?? []) {
      const words = wordsByDeck[deck.id];
      // A deck whose words are not loaded yet shows no bar rather than a wrong one.
      if (!words || (words.length === 0 && deck.word_count > 0)) continue;
      result[deck.id] = deckProgress(classifyDeck(words, learner, lists?.deferrals ?? {}));
    }
    return result;
  }, [lists, wordsByDeck, learner]);

  const decks = lists?.decks ?? [];
  const mine = decks.filter((d) => !!d.owner_id);
  const publicDecks = decks.filter((d) => !d.owner_id);
  const groups = groupPublicDecks(publicDecks);

  if (view.kind === 'session') {
    if (!isActive) return null;
    const leave = () => {
      openView(view.back);
      void refreshAfterChange();
    };
    return (
      <Flex vertical gap={12}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          style={{ alignSelf: 'flex-start' }}
          onClick={leave}
        >
          Back to {view.back.kind === 'deck' ? 'the deck' : 'decks'}
        </Button>
        {/* Full-width wrapper: the swipe card centres itself with auto margins, which would shrink
            it to its content inside a flex column. */}
        <div style={{ width: '100%' }}>
          <DeckSession
            deckId={view.deckId}
            userId={userId}
            exitLabel={view.back.kind === 'deck' ? 'Back to the deck' : 'Back to decks'}
            onExit={leave}
          />
        </div>
      </Flex>
    );
  }

  if (view.kind === 'deck') {
    return (
      <DeckPage
        key={view.deckId}
        deckId={view.deckId}
        userId={userId}
        isActive={isActive}
        onBack={() => {
          openView({ kind: 'list' });
          void refreshAfterChange();
          if (api) void loadWords(api, [view.deckId]);
        }}
        onGoThrough={(deckId) => setView({ kind: 'session', deckId, back: view })}
        onChanged={onChanged}
      />
    );
  }

  if (view.kind === 'import') {
    return (
      <Flex vertical gap={12}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          style={{ alignSelf: 'flex-start' }}
          onClick={() => openView({ kind: 'list' })}
        >
          Back to decks
        </Button>
        <ImportPanel
          onChanged={() => void refreshAfterChange()}
          onOpenDeck={(deckId) => openView({ kind: 'deck', deckId })}
        />
      </Flex>
    );
  }

  const card = (deck: CloudDeck, large?: boolean) => (
    <DeckCard
      key={deck.id}
      deck={deck}
      large={large}
      progress={progressOf[deck.id] ?? null}
      following={lists?.followingId === deck.id}
      onGoThrough={() => setView({ kind: 'session', deckId: deck.id, back: { kind: 'list' } })}
      onToggleFollow={() => void toggleFollow(deck)}
      onOpen={() => openView({ kind: 'deck', deckId: deck.id })}
    />
  );

  return (
    <Flex vertical gap={24}>
      {offline && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          You're offline. Changes are saved and sent when you're back online.
        </Text>
      )}

      <Flex vertical gap={12}>
        <Flex align="center" gap={12} wrap>
          <Title level={4} style={{ margin: 0 }}>
            Your decks
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Your own word lists.
          </Text>
          <span style={{ flexGrow: 1 }} />
          <Button onClick={() => openView({ kind: 'import' })} {...tourTarget('import')}>
            Import words
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewDeckOpen(true)}>
            New deck
          </Button>
        </Flex>
        {lists === null ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : mine.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No decks of your own yet. Make one or import a word list."
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 16,
            }}
          >
            {mine.map((d) => card(d, true))}
          </div>
        )}
      </Flex>

      <Divider style={{ margin: 0 }} />

      <Flex vertical gap={14} {...tourTarget('public-decks')}>
        <Flex align="baseline" gap={12} wrap>
          <Title level={4} style={{ margin: 0 }}>
            Public decks
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Made by LingoPing, free for everyone.
          </Text>
        </Flex>
        {lists === null ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : groups.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No public decks yet. They appear here once you're connected."
          />
        ) : (
          groups.map((group) => (
            <Flex key={group.name} vertical gap={8}>
              <Text strong style={{ fontSize: 13, color: '#475569' }}>
                {group.name}
              </Text>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 16,
                }}
              >
                {group.decks.map((d) => card(d))}
              </div>
            </Flex>
          ))
        )}
      </Flex>

      <Modal
        title="New deck"
        open={newDeckOpen}
        onCancel={() => setNewDeckOpen(false)}
        onOk={() => void createDeck()}
        okText="Create deck"
        okButtonProps={{ disabled: !newDeckName.trim() }}
        destroyOnHidden
      >
        <Flex vertical gap={6}>
          <label htmlFor="new-deck-title" style={{ fontSize: 13, color: '#475569' }}>
            Deck name
          </label>
          <Input
            id="new-deck-title"
            value={newDeckName}
            maxLength={120}
            onChange={(e) => setNewDeckName(e.target.value)}
            onPressEnter={() => void createDeck()}
            autoFocus
          />
          <Text type="secondary" style={{ fontSize: 13 }}>
            You add words on the next page, one per line.
          </Text>
        </Flex>
      </Modal>
    </Flex>
  );
};
